<template>
    <app-layout v-if="isPageReady">
        <common-content-spacer height="medium">
            <common-content-spacer height="small">
                <common-breadcrumbs :breadcrumbs="pageBreadcrumbs"></common-breadcrumbs>

                <common-page-header :heading="pageTitle"></common-page-header>
            </common-content-spacer>

            <common-page-split class="page-split__referrals">
                <template v-if="!isMobileLarge" #aside>
                    <referrals-show-details :referral="referral" :type="$route.params.type"></referrals-show-details>
                </template>

                <template #main>
                    <template v-if="isUpdateSlotAction">
                        <referrals-status
                            :referral="referral"
                            :isInline="true"
                            :description="updateSlotStatusDescription"
                            :descriptionStrike="updateSlotStatusDescriptionStrike"
                        ></referrals-status>

                        <common-content-wrapper width="huge">
                            <referrals-new-schedule
                                :physician="physician"
                                :practice="practice"
                                :location="location"
                                :isUpdate="isUpdateSlotAction"
                                @selectSlot="selectSlot($event)"
                            ></referrals-new-schedule>
                        </common-content-wrapper>
                    </template>
                    <template v-else-if="isRateReferredAction">
                        <referrals-rate-referred :referral="referral"></referrals-rate-referred>
                    </template>
                    <template v-else-if="isAddReportAction">
                        <referrals-add-report ref="referralAddNotesComponent" :referral="referral"></referrals-add-report>
                    </template>
                    <template v-else>{{ $t('pages.referrals.update.action_not_allowed') }}</template>
                </template>
            </common-page-split>
        </common-content-spacer>
        <upload-leave-modal
            :isOpen="showUploadLeaveModal"
            @close="goToNextRoute"
            @send-report="cancelNextRouteAndSendReport"
        ></upload-leave-modal>
    </app-layout>
</template>

<script>
    import { composeDisplayName } from 'components/common/CommonUserName'
    import PageWithTitleMixin from 'mixins/PageWithTitleMixin'
    import PageWithElementsMixin from 'mixins/PageWithElementsMixin'
    import PageWithDataMixin from 'mixins/PageWithDataMixin'
    import AppLayout from 'components/layouts/AppLayout'
    import ReferralsAPI from 'api/ReferralsAPI'
    import PracticesAPI from 'api/PracticesAPI'
    import ReferralsRateReferred from 'components/page-elements/ratings/ReferralsRateReferred'
    import ReferralsAddReport from 'components/page-elements/ReferralsAddReport'
    import ReferralsNewSchedule from 'components/page-elements/referrals/ReferralsNewSchedule'
    import UploadLeaveModal from 'components/page-elements/referrals/UploadLeaveModal'
    import ReferralsShowDetails from 'components/page-elements/referrals/ReferralsShowDetails'
    import ScreenSizeDetectionMixin from 'mixins/ScreenSizeDetectionMixin'
    import dayjs from 'dayjs'
    import DemoReferral from 'services/DemoReferral'

    export default {
        mixins: [PageWithTitleMixin, PageWithElementsMixin, PageWithDataMixin, ScreenSizeDetectionMixin],

        components: {
            AppLayout,
            ReferralsRateReferred,
            ReferralsAddReport,
            UploadLeaveModal,
            ReferralsNewSchedule,
            ReferralsShowDetails,
        },

        data() {
            return {
                referral: {},
                physician: this.$auth.getActiveMember(),
                nextPage: null,
                showUploadLeaveModal: false,
            }
        },

        loadData(self) {
            let route = self.$route

            if (self.$auth.isUserPracticeDemo()) {
                return {
                    referral: DemoReferral.getConfirmedReferral(),
                }
            }

            if (self.isUpdateSlotRoute(route)) {
                return {
                    location: PracticesAPI.getMy().then((p) => {
                        let loc = p.locations.find((loc) => loc.id == route.query.practiceLocationId)

                        if (loc) return loc
                        //primaryLocationAddress == practice(address fields)
                        else
                            return {
                                id: 0,
                                name: p.name,
                                practiceId: p.id,
                                address: p.address,
                                city: p.city,
                                state: p.state,
                                zip: p.zip,
                                phone: p.phone,
                                coordinates: p.coordinates,
                                distance: p.distance,
                            }
                    }),
                    practice: PracticesAPI.getMy(),
                    referral: ReferralsAPI.get(route.params.id, self.$auth.getActiveMember().id),
                }
            } else if (self.isRateReferredRoute(route) || self.isAddReportRoute(route)) {
                return {
                    referral: ReferralsAPI.get(route.params.id, self.$auth.getActiveMember().id),
                }
            }
        },

        computed: {
            physicianName() {
                return composeDisplayName(this.referral[this.$route.params.type === 'received' ? 'fromUser' : 'toUser'] || {})
            },

            pageTitle() {
                return this.$t(`pages.referrals.show.titles.${this.$route.params.type}`, {
                    name: this.physicianName,
                })
            },

            pageBreadcrumbs() {
                return [
                    {
                        label: this.$t('pages.referrals.index.breadcrumbs.referrals'),
                        route: { name: `referrals.${this.$route.params.type}.referrals` },
                    },
                    {
                        label: this.$t(`pages.referrals.index.breadcrumbs.${this.$route.params.type}`),
                        route: { name: `referrals.${this.$route.params.type}.referrals` },
                    },
                    {
                        label: this.$t(`pages.referrals.show.breadcrumbs.${this.$route.params.type}`, {
                            name: this.physicianName,
                        }),
                    },
                ]
            },

            isUpdateSlotAction() {
                return this.isUpdateSlotRoute(this.$route)
            },

            updateSlotStatusDescription() {
                if (this.isUpdateSlotAction) return this.$t('pages.referrals.update.update_slot.original_appointment_time')
                else return ''
            },

            updateSlotStatusDescriptionStrike() {
                if (this.isUpdateSlotAction) return dayjs(this.referral.appointmentDateTime).format('MMMM D, hh:mm a')
                else return ''
            },

            isRateReferredAction() {
                return this.isRateReferredRoute(this.$route)
            },

            isAddReportAction() {
                return this.isAddReportRoute(this.$route)
            },
        },

        methods: {
            selectSlot(slot) {
                ReferralsAPI.updateAvailabilitySlot(this.referral.id, slot.slot).then(() => {
                    this.redirectToShowPage({
                        successfully_rescheduled: 1,
                    })
                })
            },

            isUpdateSlotRoute(route) {
                return route.params.action === 'update-slot'
            },

            isRateReferredRoute(route) {
                return route.params.action === 'rate-referred'
            },

            isAddReportRoute(route) {
                return route.params.action === 'add-report'
            },

            redirectToShowPage(query) {
                this.$router.push({
                    name: `referrals.received.show`,
                    params: {
                        type: 'received',
                        id: this.referral.id,
                    },
                    query: query,
                })
            },

            changeReferralData(data) {
                this.referral = { ...this.referral, ...data }
            },

            openUploadLeaveModal() {
                this.showUploadLeaveModal = true
            },

            closeUploadLeaveModal() {
                this.showUploadLeaveModal = false
            },

            goToNextRoute() {
                this.showUploadLeaveModal = false
                this.nextPage()
            },

            cancelNextRouteAndSendReport() {
                this.showUploadLeaveModal = false
                this.$refs.referralAddNotesComponent.sendFiles()
            },
        },

        beforeRouteLeave(to, from, next) {
            if (this.$auth.isUserPracticeDemo()) {
                next()
                return false
            }

            if (from.params.action === 'add-report' && this.$auth.getToken()) {
                ReferralsAPI.getConsultationNotes(this.referral.id).then((files) => {
                    if (!files.length || !!files.find((file) => file.isFileSent)) {
                        next()
                        return
                    }
                    this.nextPage = next
                    this.openUploadLeaveModal()
                })
            } else {
                next()
            }
        },
    }
</script>
